/**
 * useSectionBadges — numbered "new since you last looked" counts per nav
 * section (reports, mission log, tasks, meetings, announcements, team…) plus
 * live pending counts for approval queues (access requests, leave).
 *
 * Read-only: "last seen" times live in this browser's localStorage per user
 * (smc_seen_<uid>), so nothing is written to Firestore. Opening a section
 * marks it seen. A first visit starts every section at "now" (no flood).
 */
import { useEffect, useMemo, useState } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { PORTAL_CARDS } from '../constants/portalCards';

const DB = () => getFirestore(getApp());
const KEY = uid => `smc_seen_${uid}`;
const ms = ts => (!ts ? 0 : ts.toMillis ? ts.toMillis() : ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime() || 0);

function readSeen(uid) {
  try { return JSON.parse(localStorage.getItem(KEY(uid)) || 'null'); } catch (_) { return null; }
}
function writeSeen(uid, seen) {
  try { localStorage.setItem(KEY(uid), JSON.stringify(seen)); } catch (_) { /* private mode */ }
}

/**
 * @param {object}   o
 * @param {string}   o.userId
 * @param {string}   o.role        app role (admin / ops / dept id …)
 * @param {string}   o.activeTab   currently open section — marked seen
 * @param {string}   [o.scope]     'main' | 'ops' | 'dept'
 * @param {string}   [o.deptId]    department id for scope 'dept'
 * @param {object}   [o.alias]     map badge source → tab id in this view, e.g. { reports: 'feed' }
 */
export default function useSectionBadges({ userId, role, activeTab, scope = 'main', deptId, alias = {} }) {
  const [seen, setSeen] = useState(() => {
    if (!userId) return {};
    const s = readSeen(userId);
    if (s) return s;
    const init = { _base: Date.now() };
    writeSeen(userId, init);
    return init;
  });
  const [items, setItems] = useState({});  // source -> [{ t, by }]
  const [pending, setPending] = useState({}); // source -> count

  // Reload when the signed-in user changes
  useEffect(() => {
    if (!userId) return;
    const s = readSeen(userId) || { _base: Date.now() };
    writeSeen(userId, s);
    setSeen(s);
  }, [userId]);

  const isAdmin = role === 'admin';
  const isApprover = isAdmin || role === 'ops';

  useEffect(() => {
    if (!userId) return undefined;
    const db = DB();
    const put = (k, list) => setItems(p => ({ ...p, [k]: list }));
    const subs = [];
    const watch = (k, q, map) => subs.push(onSnapshot(q, s => put(k, s.docs.map(d => map(d.data()))), () => {}));

    // Reports
    if (scope === 'dept' && deptId) {
      const built = PORTAL_CARDS.find(p => p.id === deptId && p.collection);
      if (built) {
        const f = built.dateField || 'uploadedAt';
        watch('reports', query(collection(db, built.collection), orderBy(f, 'desc'), limit(30)),
          d => ({ t: ms(d[f]), by: d[built.authorUidField || 'uploadedByUid'] }));
      } else {
        watch('reports', query(collection(db, 'dept_posts'), where('deptId', '==', deptId)),
          d => ({ t: ms(d.uploadedAt), by: d.uploadedByUid }));
      }
    } else {
      const parts = {};
      PORTAL_CARDS.filter(p => p.collection && !p.restricted).forEach(p => {
        const f = p.dateField || 'uploadedAt';
        subs.push(onSnapshot(query(collection(db, p.collection), orderBy(f, 'desc'), limit(20)), s => {
          parts[p.id] = s.docs.map(d => ({ t: ms(d.data()[f]), by: d.data()[p.authorUidField || 'uploadedByUid'] }));
          put('reports', Object.values(parts).flat());
        }, () => {}));
      });
      subs.push(onSnapshot(query(collection(db, 'dept_posts'), orderBy('uploadedAt', 'desc'), limit(30)), s => {
        parts._dept = s.docs.map(d => ({ t: ms(d.data().uploadedAt), by: d.data().uploadedByUid }));
        put('reports', Object.values(parts).flat());
      }, () => {}));
    }

    // Mission log (others' entries)
    watch('daily-log', query(collection(db, 'daily_updates'), orderBy('timestamp', 'desc'), limit(30)),
      d => ({ t: ms(d.timestamp), by: d.authorUid, hide: d.isDeleted === true }));

    // Tasks assigned to me
    watch('tasks', query(collection(db, 'tasks'), where('assigneeId', '==', userId)),
      d => ({ t: ms(d.createdAt), by: d.createdBy || d.assignedBy, hide: d.status === 'completed' }));

    // Meetings I'm invited to
    watch('meetings', query(collection(db, 'meetings'), orderBy('createdAt', 'desc'), limit(20)),
      d => ({ t: ms(d.createdAt), by: d.createdBy,
        hide: d.status === 'cancelled' || !(d.invitees === 'all' || (d.invitees || []).includes(userId)) }));

    // Announcements
    watch('announcements', query(collection(db, 'announcements'), orderBy('createdAt', 'desc'), limit(20)),
      d => ({ t: ms(d.createdAt), by: d.createdBy || d.authorUid }));

    // New crew (admin)
    if (isAdmin) {
      watch('team', collection(db, 'authorized_users'), d => ({ t: ms(d.approvedAt), by: null }));
      // REQUESTS = pending access requests + unanswered team requests
      subs.push(onSnapshot(query(collection(db, 'access_requests'), where('status', '==', 'pending')),
        s => setPending(p => ({ ...p, _access: s.size, requests: s.size + (p._team || 0) })), () => {}));
      subs.push(onSnapshot(query(collection(db, 'feedback'), where('type', '==', 'request')),
        s => {
          const n = s.docs.filter(d => (d.data().status || 'new') === 'new').length;
          setPending(p => ({ ...p, _team: n, requests: n + (p._access || 0) }));
        }, () => {}));
    }
    if (isApprover) {
      subs.push(onSnapshot(query(collection(db, 'leave_requests'), where('status', '==', 'pending')),
        s => setPending(p => ({ ...p, attendance: s.size })), () => {}));
    }
    return () => subs.forEach(u => u());
  }, [userId, scope, deptId, isAdmin, isApprover]);

  // Opening a section marks it seen
  const aliasKey = JSON.stringify(alias);
  const sourceOf = useMemo(() => {
    const m = {};
    Object.entries(JSON.parse(aliasKey)).forEach(([src, tab]) => { m[tab] = src; });
    return m;
  }, [aliasKey]);
  useEffect(() => {
    if (!userId || !activeTab) return;
    const src = sourceOf[activeTab] || activeTab;
    setSeen(prev => {
      const next = { ...prev, [src]: Date.now() };
      writeSeen(userId, next);
      return next;
    });
  }, [userId, activeTab, sourceOf]);

  return useMemo(() => {
    const alias = JSON.parse(aliasKey);
    const out = {};
    const base = seen._base || 0;
    Object.entries(items).forEach(([src, list]) => {
      const since = seen[src] || base;
      const n = list.filter(x => !x.hide && x.t > since && x.by !== userId).length;
      if (n) out[alias[src] || src] = n;
    });
    Object.entries(pending).forEach(([src, n]) => {
      if (n && !src.startsWith('_')) { const tab = alias[src] || src; out[tab] = Math.max(out[tab] || 0, n); }
    });
    // Items arriving while the section is open count as seen
    if (activeTab && !pending[sourceOf[activeTab] || activeTab]) delete out[activeTab];
    return out;
  }, [items, pending, seen, aliasKey, sourceOf, activeTab, userId]);
}
